"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Database } from "../types/supabase";

export const upcomingAutoPostQueryKey = (userId: string | undefined) =>
  ["auto-post-upcoming", userId] as const;

export type UpcomingAutoPostSummary = {
  count: number;
  nextPostAt: string | null;
};

const EMPTY_UPCOMING_AUTO_POST_SUMMARY: UpcomingAutoPostSummary = { count: 0, nextPostAt: null };

/** Keep counts exact without downloading projects, publications or video metadata. */
export const createFetchUpcomingAutoPostSummary = (
  supabase: Pick<SupabaseClient<Database>, "from">,
  userId: string | undefined,
) => async (signal?: AbortSignal): Promise<UpcomingAutoPostSummary> => {
  if (!userId) throw new Error("User not found");
  const folderIds: string[] = [];
  let cursor: string | undefined;
  // auto_folder_id intentionally has no FK: published posts survive deletion of
  // their project. Read the enabled IDs first instead of relying on a join.
  for (;;) {
    let query = supabase.from("folders").select("id")
      .eq("author_id", userId).eq("auto_publish->>enabled", "true")
      .order("id", { ascending: true }).limit(500);
    if (cursor) query = query.gt("id", cursor);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) throw error;
    if (!data?.length) break;
    folderIds.push(...data.map(folder => folder.id));
    cursor = data[data.length - 1].id;
  }

  const summary: UpcomingAutoPostSummary = { count: 0, nextPostAt: null };
  const now = new Date().toISOString();
  // Bound the URL length of the `in` filter, even for very large libraries.
  for (let offset = 0; offset < folderIds.length; offset += 200) {
    let query = supabase.from("publications").select("scheduled_at", { count: "exact" })
      .eq("owner_id", userId).eq("status", "scheduled")
      .in("auto_folder_id", folderIds.slice(offset, offset + 200))
      .gt("scheduled_at", now).order("scheduled_at", { ascending: true }).limit(1);
    if (signal) query = query.abortSignal(signal);
    const { data, count, error } = await query;
    if (error) throw error;
    if (count === null) throw new Error("Auto post count unavailable");
    summary.count += count;
    const nextPostAt = data?.[0]?.scheduled_at;
    if (nextPostAt && (!summary.nextPostAt || Date.parse(nextPostAt) < Date.parse(summary.nextPostAt))) {
      summary.nextPostAt = nextPostAt;
    }
  }
  return summary;
};

/** A count whose first post is due is stale until the next read confirms it. */
export const currentUpcomingAutoPostCount = (summary: UpcomingAutoPostSummary | undefined, now = Date.now()) =>
  summary?.nextPostAt && Date.parse(summary.nextPostAt) > now ? summary.count : 0;

/** Shared by desktop/mobile navigation; never blocks the publishing query refresh. */
export function useUpcomingAutoPostSummary({ enabled = true }: { enabled?: boolean } = {}): UpcomingAutoPostSummary {
  const supabase = useSupabaseClient();
  const userId = useSession()?.user.id;
  const active = enabled && Boolean(userId);
  const queryClient = useQueryClient();
  const [, tick] = useState(0);
  const query = useQuery({
    queryKey: upcomingAutoPostQueryKey(userId),
    queryFn: ({ signal }) => createFetchUpcomingAutoPostSummary(supabase, userId)(signal),
    enabled: active,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!active) return;
    // Publication flows await refetchQueries(["publications"], throwOnError).
    // Keep this optional nav query separate, and react to their cache updates.
    return queryClient.getQueryCache().subscribe(event => {
      if (event.type !== "updated" || event.query.queryKey[0] !== "publications" ||
        (event.action.type !== "invalidate" && event.action.type !== "success")) return;
      void queryClient.invalidateQueries({ queryKey: upcomingAutoPostQueryKey(userId) }, { cancelRefetch: false });
    });
  }, [active, queryClient, userId]);

  const nextPostAt = query.data?.nextPostAt;
  const refetch = query.refetch;
  useEffect(() => {
    if (!active || !nextPostAt || query.isError) return;
    const nextTime = Date.parse(nextPostAt);
    if (!Number.isFinite(nextTime)) return;
    const timeout = window.setTimeout(() => {
      tick(value => value + 1);
      void refetch({ cancelRefetch: false });
    }, Math.max(1, Math.min(nextTime - Date.now() + 1, 2_147_483_647)));
    return () => window.clearTimeout(timeout);
  }, [active, nextPostAt, query.dataUpdatedAt, query.isError, refetch]);

  const summary = active && !query.isError ? query.data : undefined;
  return summary && currentUpcomingAutoPostCount(summary) > 0 ? summary : EMPTY_UPCOMING_AUTO_POST_SUMMARY;
}

/** Compatibility for surfaces that only need the count. */
export function useUpcomingAutoPostCount(options: { enabled?: boolean } = {}): number {
  return useUpcomingAutoPostSummary(options).count;
}
